import {
  ReactNode,
  createContext,
  useContext,
  useReducer,
  useMemo,
  useEffect,
} from 'react'

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  ICognitoStorage,
} from 'amazon-cognito-identity-js'
import MemoryStorage from 'memorystorage'
import { API_URL } from 'api'
import { hasLocalStorage } from 'utils'
import * as access from 'types/access'
import ReactGA from 'react-ga4'
import { hasAccess } from 'utils/access'
import Intercom, { shutdown } from '@intercom/messenger-js-sdk'
import { IntercomSettings } from '@intercom/messenger-js-sdk/dist/types'

// Use memory storage if local storage is disabled
const memory = new MemoryStorage()
const localStoragePolyfill = hasLocalStorage() ? window.localStorage : memory

const {
  REACT_APP_USER_POOL_ID,
  REACT_APP_USER_POOL_CLIENT_ID,
  REACT_APP_AUTH_ENDPOINT,
} = process.env

const INTERCOM_SETTINGS: IntercomSettings = {
  app_id: process.env.REACT_APP_INTERCOM_APP_ID as string,
  region: 'eu',
  api_base: 'https://api-iam.eu.intercom.io',
}

type State = {
  loading: boolean | undefined
  user: any
  profile: any
}

type Action = {
  type: 'restore_session' | 'refresh_session' | 'login' | 'logout' | 'verify'
  user: State['user']
  profile: State['profile']
  loading?: State['loading']
}

type ContextType = {
  loading: boolean | undefined
  user: State['user']
  profile: State['profile']
  login: (email: string) => Promise<void>
  logout: (email: string) => Promise<void>
  verify: (code: string) => Promise<void>
  isAuthenticated: () => Promise<boolean>
  hasAccess: any
}

type CurrentUserSession = {
  user?: CognitoUser
  session: CognitoUserSession
}

const AuthContext = createContext({} as ContextType)

const userPool: CognitoUserPool = new CognitoUserPool({
  UserPoolId: REACT_APP_USER_POOL_ID as string,
  ClientId: REACT_APP_USER_POOL_CLIENT_ID as string,
  Storage: localStoragePolyfill as ICognitoStorage,
  endpoint: REACT_APP_AUTH_ENDPOINT as string,
})

const reducer = (prevState: State, action: Action): State => {
  switch (action.type) {
    case 'login':
      return {
        ...prevState,
        user: action.user,
        profile: action.profile,
        loading: false,
      }

    case 'logout':
      return {
        ...prevState,
        user: action.user,
        profile: action.profile,
        loading: false,
      }

    case 'restore_session':
      return {
        user: action.user,
        profile: action.profile,
        loading: false,
      }

    case 'refresh_session':
      return {
        ...prevState,
        profile: {
          ...prevState.profile,
          ...action.profile,
        },
        loading: false,
      }

    case 'verify':
      return {
        ...prevState,
        user: action.user,
        profile: action.profile,
        loading: action.loading,
      }

    default:
      return prevState
  }
}

export const getCurrentUserSession = async () => {
  const user = userPool.getCurrentUser()
  if (!user) {
    throw Error('User not found')
  }
  return new Promise<CurrentUserSession>((resolve, reject) => {
    user &&
      user.getSession((err: any, session: any) => {
        if (err) {
          reject(err)
        } else {
          resolve({
            user,
            session,
          })
        }
      })
  })
}

async function getProfile(idToken: string, id: string) {
  const res = await fetch(`${API_URL}/user/${id}`, {
    method: 'GET',
    headers: {
      Authorization: `${idToken}`,
    },
  })
  return res.json()
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {
    loading: true,
    user: null,
    profile: null,
  })

  // Bootstrap on mount
  useEffect(() => {
    async function bootstrap() {
      try {
        const { user, session } = await getCurrentUserSession()

        // Retrieve teacher information
        const profile = await getProfile(
          session.getAccessToken().getJwtToken(),
          session.getIdToken().payload.sub
        )

        const payload = await session.getIdToken().payload

        const groups = payload['cognito:groups']
        const accessToken = payload['custom:access']

        dispatch({
          type: 'restore_session',
          user,
          profile: { ...profile.user, groups, accessToken },
        })
      } catch (e) {
        dispatch({ type: 'restore_session', user: null, profile: null })
      }
    }
    bootstrap()
  }, [])

  // Track user on Google Analytics 4 with cognito id
  useEffect(() => {
    if (state.profile?.id) {
      ReactGA.set({ userId: state.profile.id })
    }
  }, [state.profile?.id])

  const authContext = useMemo(
    () => ({
      login: async (email: string) => {
        try {
          const user = new CognitoUser({
            Username: email,
            Pool: userPool,
          })

          user.setAuthenticationFlowType('CUSTOM_AUTH')

          // Magic link sent
          const authDetails = new AuthenticationDetails({
            Username: email,
          })

          localStoragePolyfill.setItem(
            'tm__session',
            JSON.stringify(
              await new Promise((resolve, reject) => {
                user.initiateAuth(authDetails, {
                  onSuccess: resolve,
                  onFailure: reject,
                  customChallenge: () => {
                    // @ts-ignore
                    resolve({ username: user.username, Session: user.Session })
                  },
                })
              })
            )
          )
        } catch (e: any) {
          if (e.code === 'NotAuthorizedException') {
            throw new Error('Not authorized.')
          } else if (e.code === 'UserNotFoundException') {
            throw new Error('User not found.')
          }
          throw new Error('Something went wrong, please try again.')
        }
        dispatch({ type: 'login', user: null, profile: null })
      },
      logout: async (email: string) => {
        localStoragePolyfill.clear()
        userPool.getCurrentUser()?.signOut()
        localStoragePolyfill.setItem(email, 'true')
        dispatch({ type: 'logout', user: null, profile: null })
      },
      verify: async (code: string) => {
        try {
          const user = localStoragePolyfill.getItem('tm__session')
          if (!user) throw new Error('No user found in localStorage')

          // @ts-ignore
          const currentUser = JSON.parse(user)

          const cognitoUser: CognitoUser = new CognitoUser({
            Username: currentUser.username,
            Pool: userPool,
            Storage: localStoragePolyfill as ICognitoStorage,
          })
          // @ts-ignore
          cognitoUser.Session = currentUser.Session
          cognitoUser.setAuthenticationFlowType('CUSTOM_AUTH')
          dispatch({ type: 'verify', user: null, profile: null, loading: true })

          await new Promise((resolve, reject) => {
            cognitoUser.sendCustomChallengeAnswer(code, {
              onSuccess: resolve,
              onFailure: reject,
            })
          })

          const userSession: CognitoUserSession = await new Promise(
            (resolve, reject) => {
              cognitoUser.getSession((err: any, session: any) => {
                if (err) {
                  reject(err)
                  return
                } else {
                  resolve(session)
                  return
                }
              })
            }
          )
          // Retrieve teacher information
          const profile = await getProfile(
            userSession.getAccessToken().getJwtToken(),
            userSession.getIdToken().payload.sub
          )

          const payload = await userSession.getIdToken().payload

          const groups = payload['cognito:groups']
          const accessToken = payload['custom:access']

          dispatch({
            type: 'verify',
            user: cognitoUser,
            profile: { ...profile.user, groups, accessToken },
            loading: false,
          })
        } catch (e) {
          console.error('sendCustomChallengeAnswer: ', e)
          dispatch({ type: 'verify', user: null, profile: null })
        }
      },
      isAuthenticated: async () => {
        try {
          await new Promise((resolve, reject) => {
            state.user.getSession((err: any, session: any) => {
              if (err) {
                reject(err)
                return
              } else {
                resolve(session)
                return
              }
            })
          })
          return true
        } catch {
          return false
        }
      },
      hasAccess: (group: access.Groups, flag: any) => {
        if (state?.profile?.accessToken) {
          return hasAccess(state.profile.accessToken, group, flag)
        }

        return false
      },
      loading: state.loading,
      user: state.user,
      profile: state.profile,
    }),
    [state.loading, state.user, state.profile]
  )

  useEffect(() => {
    if (state.profile) {
      const { id, hash } = state.profile.intercom ?? {}

      Intercom({
        ...INTERCOM_SETTINGS,
        user_id: id,
        user_hash: hash,
      })
    } else {
      shutdown()
    }
  }, [state.profile])

  return (
    <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>
  )
}

export const useAuth = () => {
  const {
    loading,
    user,
    profile,
    login,
    logout,
    verify,
    isAuthenticated,
    hasAccess,
  } = useContext(AuthContext)
  return {
    loading,
    user,
    profile,
    login,
    logout,
    verify,
    isAuthenticated,
    hasAccess,
  }
}
